import { useSearchParams } from "react-router-dom";
import { Clock, Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import { PageHero } from "@/components/layout/PageHero";
import { EnquiryForm } from "@/components/EnquiryForm";
import { Reveal } from "@/components/Reveal";
import { useSite } from "@/context/SiteContext";

const ContactPage = () => {
  const { content } = useSite();
  const [params] = useSearchParams();
  const slug = params.get("service");
  const quoteMode = params.get("quote") === "1" || Boolean(slug);
  const preset = slug ? content?.services?.find((s) => s.slug === slug)?.title || "" : "";

  const phone = content?.phone || "+91 90000 00000";
  const whatsapp = (content?.whatsapp || "919000000000").replace(/[^0-9]/g, "");
  const email = content?.email || "info@sssgroups.com";

  const actions = [
    { icon: Phone, label: "Call Us", value: phone, href: `tel:${phone.replace(/\s/g, "")}`, testid: "contact-call-card" },
    { icon: MessageCircle, label: "WhatsApp", value: "Chat with our team", href: `https://wa.me/${whatsapp}`, testid: "contact-whatsapp-card" },
    { icon: Mail, label: "Email", value: email, href: `mailto:${email}`, testid: "contact-email-card" },
  ];

  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow={quoteMode ? "Request a Quote" : "Contact Us"}
        title={quoteMode ? "Tell Us What You Need" : "Let's Start a Conversation"}
        description="Reach us by phone, WhatsApp, or email — or send an enquiry and our team will respond within one business day."
      />

      <section className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid gap-6 sm:grid-cols-3">
            {actions.map(({ icon: Icon, label, value, href, testid }, i) => (
              <Reveal key={label} delay={i * 0.08}>
                <a
                  href={href}
                  target={href.startsWith("http") ? "_blank" : undefined}
                  rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
                  data-testid={testid}
                  className="group flex h-full items-center gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-gold hover:shadow-lg hover:shadow-gold/10"
                >
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-navy-900 text-gold transition-colors duration-300 group-hover:bg-gold group-hover:text-navy-950">
                    <Icon className="h-6 w-6" />
                  </span>
                  <span>
                    <span className="block text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
                    <span className="mt-0.5 block text-sm font-bold text-navy-900">{value}</span>
                  </span>
                </a>
              </Reveal>
            ))}
          </div>

          <div className="mt-16 grid gap-10 lg:grid-cols-5">
            <Reveal delay={0.1} className="lg:col-span-3">
              <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-xl shadow-slate-200/60 sm:p-10">
                <h2 className="font-serif text-2xl font-bold text-navy-900">
                  {quoteMode ? "Request a Quote" : "Send Us an Enquiry"}
                </h2>
                <p className="mb-8 mt-2 text-sm text-slate-500">
                  Fields marked <span className="text-gold-dark">*</span> are required. Your information is stored securely and never shared.
                </p>
                <EnquiryForm defaultService={preset} quoteMode={quoteMode} />
              </div>
            </Reveal>
            <Reveal delay={0.18} className="lg:col-span-2">
              <div className="grain-overlay relative h-full overflow-hidden rounded-2xl bg-navy-900 p-9">
                <h3 className="font-serif text-2xl font-bold text-white">SIREE SOLUTIONS GROUPS</h3>
                <p className="mt-1 font-mono text-xs font-semibold uppercase tracking-[0.3em] text-gold">SSS GROUPS</p>
                <ul className="mt-8 space-y-6 text-sm text-slate-300">
                  <li className="flex items-start gap-3">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                    <span>{content?.address || "Office address — to be updated"}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Clock className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                    <span>{content?.hours || "Monday – Saturday: 9:00 AM – 6:00 PM"}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Phone className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                    <a href={`tel:${phone.replace(/\s/g, "")}`} className="hover:text-gold">{phone}</a>
                  </li>
                  <li className="flex items-start gap-3">
                    <Mail className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                    <a href={`mailto:${email}`} className="hover:text-gold">{email}</a>
                  </li>
                </ul>
                <div className="mt-10 rounded-xl border border-gold/25 bg-gold/5 p-5">
                  <p className="font-serif text-lg italic text-gold-light">
                    "One trusted partner for every service your world runs on."
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
